import {
  AgentEvaluationCallDirection,
  AgentEvaluationPersonaType,
  AgentEvaluationProviderSelection,
  AgentEvaluationScenario,
  AgentEvaluationScenarioPayload,
  AgentEvaluationStatus,
  ProviderDefinition,
} from "../../../services/api";
import { ProviderSettingsDraft } from "../../../types/strategyConfig";

export const personaTypeOptions: {
  value: AgentEvaluationPersonaType;
  label: string;
}[] = [
  { value: "good", label: "Good" },
  { value: "bad", label: "Bad" },
  { value: "edge", label: "Edge" },
];

export const callDirectionOptions: {
  value: AgentEvaluationCallDirection;
  label: string;
}[] = [
  { value: "incoming", label: "Incoming" },
  { value: "outbound", label: "Outbound" },
];

export const defaultInstructions =
  "Generate stable QA scenarios for happy paths, confused users, bad-faith users, missing information, rude tone, unsupported requests, and multilingual language switches inside a chat.";

export const cloneSelection = (
  selection: ProviderSettingsDraft | null
): ProviderSettingsDraft | null =>
  selection
    ? {
        provider: selection.provider,
        settings: JSON.parse(JSON.stringify(selection.settings ?? {})),
      }
    : null;

export const getPreferredProviderDefinition = (
  providers: ProviderDefinition[],
  preferredId?: string | null
) =>
  providers.find((provider) => provider.id === preferredId) ??
  providers.find((provider) => provider.id === "gemini") ??
  providers.find((provider) => provider.id === "paytm_inference") ??
  providers.find((provider) => provider.id === "openrouter") ??
  providers.find((provider) => provider.id === "groq") ??
  providers[0];

export const toApiProvider = (
  selection: ProviderSettingsDraft | null
): AgentEvaluationProviderSelection | null => {
  if (!selection?.provider) return null;
  return {
    provider: selection.provider,
    settings: selection.settings ?? {},
  };
};

export const splitCommaList = (value: string): string[] =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

export const splitLines = (value: string): string[] =>
  value
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);

export const formatPercent = (score?: number | null) =>
  typeof score === "number" ? `${Math.round(score * 100)}%` : "-";

export const shortId = (id: string) => id.slice(0, 8);

export const formatRelativeTime = (input?: string | Date | null): string => {
  if (!input) return "never";
  const date = typeof input === "string" ? new Date(input) : input;
  const diffMs = Date.now() - date.getTime();
  if (Number.isNaN(diffMs)) return "—";
  const seconds = Math.round(diffMs / 1000);
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days}d ago`;
  return date.toLocaleDateString();
};

export const accuracyDelta = (
  prev?: number | null,
  curr?: number | null
): { value: number | null; direction: "up" | "down" | "flat" | "n/a" } => {
  if (typeof curr !== "number") return { value: null, direction: "n/a" };
  if (typeof prev !== "number") return { value: null, direction: "n/a" };
  const diff = curr - prev;
  if (Math.abs(diff) < 0.005) return { value: diff, direction: "flat" };
  return { value: diff, direction: diff > 0 ? "up" : "down" };
};

export const statusLabel = (status?: string) =>
  (status ?? "draft").replace("_", " ");

export const getStatusClasses = (status?: AgentEvaluationStatus | string) => {
  if (status === "passed") return "bg-green-50 text-green-700 border-green-200";
  if (status === "failed") return "bg-red-50 text-red-700 border-red-200";
  if (status === "needs_review")
    return "bg-amber-50 text-amber-700 border-amber-200";
  if (status === "running" || status === "queued")
    return "bg-blue-50 text-blue-700 border-blue-200";
  if (status === "accepted")
    return "bg-green-50 text-green-700 border-green-200";
  if (status === "rejected") return "bg-red-50 text-red-700 border-red-200";
  return "bg-[var(--muted)] text-[var(--foreground-muted)] border-[var(--border)]";
};

export const getScenarioVerdictStatus = (scenario: AgentEvaluationScenario) =>
  scenario.latest_result?.status ?? "draft";

export const hasCriticalRiskText = (value?: string | null) => {
  const text = (value ?? "").toLowerCase();
  if (!text) return false;
  if (
    text.includes("no critical") ||
    text.includes("critical failures: none") ||
    text.includes("critical failure: false")
  ) {
    return false;
  }
  return (
    text.includes("critical:") ||
    text.includes("critical failure") ||
    text.includes("critical issue") ||
    text.includes("critical regression") ||
    text.includes("regulatory/compliance") ||
    text.includes("compliance issue") ||
    text.includes("unsafe")
  );
};

export const scenarioToPayload = (
  scenario: AgentEvaluationScenario
): AgentEvaluationScenarioPayload => ({
  title: scenario.title,
  persona_name: scenario.persona_name,
  persona_type: scenario.persona_type,
  languages: scenario.languages,
  language_switching: scenario.language_switching,
  call_direction: scenario.call_direction ?? "incoming",
  user_goal: scenario.user_goal,
  starting_message: scenario.starting_message,
  expected_behavior: scenario.expected_behavior,
  pass_criteria: scenario.pass_criteria,
  failure_traps: scenario.failure_traps,
});

export const preserveScenarioCallDirections = (
  incomingScenarios: AgentEvaluationScenario[],
  currentScenarios: AgentEvaluationScenario[],
  fallbackDirection?: AgentEvaluationCallDirection | null
): AgentEvaluationScenario[] => {
  const currentDirections = new Map(
    currentScenarios.map((scenario) => [scenario.id, scenario.call_direction])
  );

  return incomingScenarios.map((scenario) => {
    const direction =
      scenario.call_direction === "outbound"
        ? "outbound"
        : currentDirections.get(scenario.id) ?? fallbackDirection ?? "incoming";

    return scenario.call_direction === direction
      ? scenario
      : { ...scenario, call_direction: direction };
  });
};

const formValue = (formData: FormData, key: string) =>
  String(formData.get(key) ?? "");

export const scenarioFormDataToPayload = (
  formData: FormData
): AgentEvaluationScenarioPayload => ({
  title: formValue(formData, "title"),
  persona_name: formValue(formData, "persona_name"),
  persona_type: formValue(formData, "persona_type") as AgentEvaluationPersonaType,
  languages: splitCommaList(formValue(formData, "languages")),
  language_switching: formData.has("language_switching"),
  call_direction: (formValue(formData, "call_direction") ||
    "incoming") as AgentEvaluationCallDirection,
  user_goal: formValue(formData, "user_goal"),
  starting_message: formValue(formData, "starting_message"),
  expected_behavior: formValue(formData, "expected_behavior"),
  pass_criteria: splitLines(formValue(formData, "pass_criteria")),
  failure_traps: splitLines(formValue(formData, "failure_traps")),
});

export const emptyScenarioPayload = (
  languages: string[],
  callDirection: AgentEvaluationCallDirection = "incoming"
): AgentEvaluationScenarioPayload => ({
  title: "New QA scenario",
  persona_name: "Manual tester",
  persona_type: "edge",
  languages: languages.length ? languages : ["English"],
  language_switching: languages.length > 1,
  call_direction: callDirection,
  user_goal: "",
  starting_message: "",
  expected_behavior: "",
  pass_criteria: [
    "Agent answers the user's actual goal without inventing unavailable details.",
  ],
  failure_traps: [
    "Wrong language",
    "Hallucinated information",
    "Missed clarification",
    "Policy miss",
  ],
});

export type PromptEditorState =
  | { mode: "current"; prompt: string }
  | { mode: "candidate"; prompt: string; candidateId: string }
  | null;
